import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import PageLayout from "@/components/layout/PageLayout";
import Seo from "@/components/foundation/Seo";
import Container from "@/components/foundation/Container";
import Section from "@/components/foundation/Section";
import Card from "@/components/foundation/Card";
import Badge from "@/components/foundation/Badge";
import Button from "@/components/foundation/Button";
import FadeIn from "@/components/foundation/FadeIn";
import GlowOrb from "@/components/foundation/GlowOrb";
import GridPattern from "@/components/foundation/GridPattern";

import PartnerDashboardMock from "@/components/marketing/PartnerDashboardMock";
import Timeline from "@/components/marketing/Timeline";
import FundingPartnerForm from "@/components/marketing/FundingPartnerForm";
import { AutoLinkPartners } from "@/components/marketing/PartnerLink";

import {
  Database,
  FileCheck2,
  Cpu,
  Repeat,
  Search,
  ShieldAlert,
  Gauge,
  RefreshCw,
  LineChart,
  Plug,
} from "lucide-react";

const ADVANTAGE = [
  {
    icon: Database,
    title: "Verified Financial Data",
    body: "Every merchant profile comes with structured, banking-sourced data via Mastercard Finicity — no self-reported spreadsheets, no gaps.",
  },
  {
    icon: FileCheck2,
    title: "Structured Underwriting Files",
    body: "Applications arrive pre-formatted with the fields you need: cash flow shape, revenue trend, balance history, document set, and ZenScore.",
  },
  {
    icon: Cpu,
    title: "AI-Enhanced Deal Insights",
    body: "Uptiq AI powers the intelligence layer, surfacing signals like deposit stability, revenue momentum, and volatility flags on every profile.",
  },
  {
    icon: Repeat,
    title: "Repayment-Ready Infrastructure",
    body: "The Phase 2 platform is being built to support live repayment monitoring and structured feedback loops back into ZenScore.",
  },
];

const PARTNER_STEPS = [
  { label: "Apply", description: "Submit a short partner application with your funding focus and target volume." },
  { label: "Verified", description: "We verify your firm, product scope, and integration preferences." },
  { label: "Access Deal Flow", description: "Receive verified merchant files with structured ZenScore-based insights." },
  { label: "Fund Deals", description: "Underwrite and fund with a level of data quality traditional brokered flow can't match." },
];

const FUTURE_CAPS = [
  { icon: Search, title: "Merchant Discovery", body: "Filter verified merchants by ZenScore, tenure, industry, and cash flow shape." },
  { icon: ShieldAlert, title: "AI Risk Analytics", body: "Volatility flags, overdraft frequency, and early-warning indicators surfaced automatically." },
  { icon: Gauge, title: "ZenScore Integration", body: "Score-native filters, thresholds, and category breakdowns built into deal review." },
  { icon: RefreshCw, title: "Repayment Tracking", body: "Live repayment status and structured feedback back into merchant scoring." },
  { icon: LineChart, title: "Portfolio Monitoring", body: "Portfolio-level dashboards for volume, cadence, and concentration analytics." },
  { icon: Plug, title: "API Connectivity", body: "Programmatic access to verified deal flow, ZenScore data, and repayment events." },
];

export default function Partners() {
  const navigate = useNavigate();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <PageLayout>
      <Seo
        title="ZenMe Capital · For Funding Partners"
        description="Access verified small-business deal flow. ZenScore-qualified merchants, secure banking data, AI-powered underwriting insights, and modern rails for funding partners."
        path="/partners"
      />

      {/* HERO */}
      <section
        className="relative overflow-hidden pt-28 pb-16 sm:pt-32 sm:pb-24"
        data-testid="partners-hero"
      >
        <GridPattern variant="dots" size={28} opacity={0.4} />
        <GlowOrb color="emerald" size={540} intensity={0.22} className="-bottom-40 -left-40" />

        <Container className="relative z-10">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-6">
              <FadeIn>
                <Badge variant="emerald" pulse>Funding Partners</Badge>
              </FadeIn>
              <FadeIn delay={0.06}>
                <h1 className="mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl text-balance">
                  Access verified businesses,{" "}
                  <span className="text-brand-emerald">not cold leads.</span>
                </h1>
              </FadeIn>
              <FadeIn delay={0.14}>
                <p className="mt-6 max-w-xl text-base text-text-secondary sm:text-lg leading-relaxed">
                  Every merchant on ZenMe is verified through secure banking
                  data, structured financial documentation, and AI-powered
                  underwriting insights.
                </p>
              </FadeIn>
              <FadeIn delay={0.22}>
                <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={() => scrollTo("partner-application")}
                    rightIcon={<ArrowRight className="h-4 w-4" />}
                    data-testid="partners-become-btn"
                  >
                    Become a Funding Partner
                  </Button>
                  <Button
                    variant="secondary"
                    size="lg"
                    onClick={() => navigate("/marketplace")}
                  >
                    Join the Marketplace Waitlist
                  </Button>
                </div>
              </FadeIn>
            </div>

            <div className="flex items-center justify-center lg:col-span-6 lg:justify-end">
              <FadeIn delay={0.2} direction="left">
                <PartnerDashboardMock />
              </FadeIn>
            </div>
          </div>
        </Container>
      </section>

      {/* THE ZENME ADVANTAGE */}
      <Section
        eyebrow="The ZenMe Advantage"
        headline="Deal flow, redefined."
        subheadline="Traditional broker submissions arrive incomplete, unverified, and inconsistent. ZenMe flips that."
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {ADVANTAGE.map((a, i) => {
            const Icon = a.icon;
            return (
              <FadeIn key={a.title} delay={0.06 * i}>
                <Card variant="elevated" className="flex h-full flex-col gap-4 p-6">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-brand-emerald/25 bg-brand-emerald/10 text-brand-emerald">
                    <Icon className="h-5 w-5" strokeWidth={1.75} />
                  </div>
                  <div>
                    <h3 className="font-display text-base font-semibold text-white">
                      {a.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-text-secondary">
                      <AutoLinkPartners text={a.body} />
                    </p>
                  </div>
                </Card>
              </FadeIn>
            );
          })}
        </div>
      </Section>

      {/* HOW PARTNERSHIP WORKS */}
      <Section
        eyebrow="How Partnership Works"
        headline="Four steps to verified deal flow."
      >
        <Timeline steps={PARTNER_STEPS} />
      </Section>

      {/* CURRENT VS FUTURE */}
      <Section
        eyebrow="Today & Tomorrow"
        headline="Where partnership starts — and where it's going."
      >
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <FadeIn>
            <Card variant="elevated" className="flex h-full flex-col gap-5 p-8">
              <Badge variant="emerald" pulse>Today</Badge>
              <h3 className="font-display text-2xl font-semibold text-white">
                Brokered submissions of verified merchant files
              </h3>
              <p className="text-sm leading-relaxed text-text-secondary">
                Right now, ZenMe operates as a brokered channel. Verified
                merchant profiles — complete with structured banking data,
                cash flow analytics, and ZenScore — are shared with approved
                funding partners for underwriting.
              </p>
              <ul className="mt-2 flex flex-col gap-2 text-sm text-white/90">
                <li>• Verified banking data</li>
                <li>• Pre-structured underwriting file</li>
                <li>• ZenScore included in every profile</li>
                <li>• Direct communication with the ZenMe team</li>
              </ul>
            </Card>
          </FadeIn>

          <FadeIn delay={0.1}>
            <Card variant="glass" interactive={false} className="flex h-full flex-col gap-5 p-8">
              <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-white/[0.12] bg-white/[0.04] px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-text-secondary">
                <span className="h-1.5 w-1.5 rounded-full bg-white/50" />
                Coming Soon
              </span>
              <h3 className="font-display text-2xl font-semibold text-white">
                Marketplace SaaS — the full partner platform
              </h3>
              <p className="text-sm leading-relaxed text-text-secondary">
                The next phase of ZenMe is a full self-serve marketplace with
                a dedicated partner dashboard, portfolio analytics, ZenScore
                integration, live repayment monitoring, and API access.
              </p>
              <ul className="mt-2 flex flex-col gap-2 text-sm text-white/90">
                <li>• Partner dashboard + portfolio views</li>
                <li>• Merchant discovery + advanced filters</li>
                <li>• Repayment tracking + performance analytics</li>
                <li>• API access for embedded workflows</li>
              </ul>
            </Card>
          </FadeIn>
        </div>
      </Section>

      {/* WHAT YOU CAN EXPECT */}
      <Section
        eyebrow="What You Can Expect"
        headline="The partner platform we're building."
        subheadline="These are the capabilities coming to the ZenMe partner platform. Early partners help shape them."
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {FUTURE_CAPS.map((f, i) => {
            const Icon = f.icon;
            return (
              <FadeIn key={f.title} delay={0.05 * i}>
                <Card variant="elevated" className="relative flex h-full flex-col p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-brand-emerald/25 bg-brand-emerald/10 text-brand-emerald">
                      <Icon className="h-4 w-4" strokeWidth={1.75} />
                    </div>
                    <span className="inline-flex items-center gap-1 rounded-full border border-white/[0.10] bg-white/[0.02] px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-text-muted">
                      Coming Soon
                    </span>
                  </div>
                  <h4 className="mt-4 font-display text-base font-semibold text-white">
                    {f.title}
                  </h4>
                  <p className="mt-1 text-sm leading-relaxed text-text-secondary">
                    {f.body}
                  </p>
                </Card>
              </FadeIn>
            );
          })}
        </div>
      </Section>

      {/* APPLICATION FORM */}
      <div id="partner-application" className="scroll-mt-24">
        <Section
          eyebrow="Apply"
          headline="Become a funding partner."
          subheadline="Tell us a little about your firm. We review every application individually and typically respond within 2 business days."
        >
          <FadeIn>
            <Card
              variant="glass"
              interactive={false}
              className="p-8 sm:p-10"
            >
              <FundingPartnerForm source="partners_page" />
            </Card>
          </FadeIn>
        </Section>
      </div>

      {/* CTA BAND */}
      <section className="relative overflow-hidden py-24 sm:py-28">
        <GlowOrb color="emerald" size={480} intensity={0.18} className="left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
        <Container className="relative z-10">
          <div className="mx-auto flex max-w-2xl flex-col items-center gap-6 text-center">
            <FadeIn>
              <h2 className="font-display text-3xl font-semibold text-white sm:text-4xl">
                Prefer to talk to us first?
              </h2>
            </FadeIn>
            <FadeIn delay={0.1}>
              <p className="text-base text-text-secondary">
                Reach out and we&rsquo;ll set up a call to walk through the
                partner experience.
              </p>
            </FadeIn>
            <FadeIn delay={0.18}>
              <Button
                variant="primary"
                size="lg"
                onClick={() => navigate("/contact")}
                rightIcon={<ArrowRight className="h-4 w-4" />}
              >
                Contact Us
              </Button>
            </FadeIn>
          </div>
        </Container>
      </section>
    </PageLayout>
  );
}
